import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Search, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import PlannerModal from "@/components/PlannerModal";

export default function Planner() {
  const [rels, setRels] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();
  // Entry-point context (Shop by Occasion / Make It More Memorable / etc.)
  // flows in via query params and is passed straight through to the planner —
  // same existing engine, no duplicate AI flow.
  const [params] = useSearchParams();
  const ctxOccasion = params.get("occasion") || "Birthday";
  const ctxExperienceId = params.get("experience") || null;
  const ctxRelId = params.get("rel") || null;
  const ctxMood = params.get("mood") || null;
  const ctxTier = params.get("tier") || null;
  const location = useLocation();

  useEffect(() => {
    // Fast path: the hero hands the full relationship object via router state —
    // open the modal synchronously so it is mounted before the AI transition
    // overlay lifts (no fetch gap → no open/close flicker).
    const stateRel = location.state?.relationship;
    if (stateRel && ctxRelId && stateRel.id === ctxRelId) {
      setSelected(stateRel);
      api.get("/relationships").then((r) => setRels(r.data || [])).catch(() => {});
      return;
    }
    api.get("/relationships").then((r) => {
      const list = r.data || [];
      setRels(list);
      // Hero CTA context: explicit relationship id wins; otherwise auto-open
      // when exactly one relationship exists and context was passed.
      if (ctxRelId) {
        const match = list.find((x) => x.id === ctxRelId);
        if (match) { setSelected(match); return; }
      }
      if (list.length === 1 && (params.get("occasion") || params.get("experience"))) {
        setSelected(list[0]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = rels.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="planner-picker-page">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full bg-white border border-rose-100 px-3 py-1.5 mb-4">
          <Sparkles className="w-3.5 h-3.5 text-violet-600" strokeWidth={2} />
          <span className="text-[11px] uppercase tracking-[0.15em] font-bold gradient-text-rose-purple">AI Celebration Planner</span>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl text-charcoal">Who are we celebrating?</h1>
        <p className="text-charcoal/60 mt-2">Pick a person and I'll draft a personalised plan.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-charcoal/40 absolute left-4 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…"
          data-testid="planner-search"
          className="w-full rounded-full bg-white border border-warm pl-11 pr-4 py-3 focus:outline-none focus:border-rose-400" />
      </div>

      {filtered.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="planner-empty">
          <p className="font-display text-2xl text-charcoal mb-2">No one yet</p>
          <p className="text-charcoal/60 mb-6">Add someone important to start planning.</p>
          <button onClick={() => navigate("/relationships?new=1")}
            className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40"
            style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
            data-testid="planner-empty-add">Add Someone</button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((r, i) => (
            <motion.button key={r.id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              onClick={() => setSelected(r)} data-testid={`planner-pick-${i}`}
              className="card-surface p-4 text-left flex items-center gap-4 hover:border-rose-200">
              {r.picture ? (
                <img src={r.picture} alt="" className="w-14 h-14 rounded-full object-cover ring-2 ring-white" />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-rose-300 to-violet-400 flex items-center justify-center font-bold text-xl text-white">
                  {r.name.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-charcoal truncate">{r.name}</p>
                <p className="text-xs text-charcoal/60">{r.relationship_type}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-charcoal/40" />
            </motion.button>
          ))}
        </div>
      )}

      {selected && (
        <PlannerModal
          relationship={selected}
          defaultOccasion={ctxOccasion}
          defaultExperienceId={ctxExperienceId}
          defaultStyle={ctxMood}
          defaultTier={ctxTier}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
